<template>
  <v-navigation-drawer
    :model-value="dataStore.getShowEditPanel"
    location="right"
    :scrim="false"
    :style="{ width: width }"
    temporary
  >
    <OperationalComponent
      type="edit"
      icon="ecc:ecc-ALLARM-DeviceHight"
      :title="$t('editAlarm')"
      :closeDialog="closeDialog"
      :advancedDialog="dataStore.openAdvanced"
      :advancedEnabled="false"
      @confirmDialog="onCreateClick"
      :confirmLoading="isFetching"
      :acceptDisabled="!confirmButtonEnabled"
    >
      <v-text-field
        :label="$t('Name')"
        v-model="name"
        density="compact"
        class="py-2"
        :error-messages="nameErrorMessage"
      ></v-text-field>
      <v-text-field
        :label="$t('description')"
        v-model="description"
        density="compact"
        hide-details
        class="py-2"
      ></v-text-field>
      <v-row class="py-2">
        <v-col cols="2" class="py-1 pr-0">
          <v-btn
            size="x-small"
            @click="toggleStateMachine"
            icon="flowchart"
            class="bg-wt squareCorners"
          />
        </v-col>
        <v-col cols="10" class="py-0 pl-0">
          <v-tooltip
            :text="$t('notPossibleToChangeTypeAfterCreation')"
            location="top"
          >
            <template v-slot:activator="{ props }">
              <v-select
                v-model="type"
                v-bind="props"
                :items="alarmTypes"
                :label="$t('type')"
                item-title="alias"
                item-value="id"
                return-object
                readonly
                @update:modelValue="toggleShowedState"
                hide-details
              ></v-select>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-select
        v-model="severity"
        :items="alarmSeverity"
        :label="$t('severity')"
        return-object
        hide-details
        class="py-2"
      ></v-select>
      <!-- Enabled -->
      <v-row class="pa-4">
        <p class="checkbox-label" data-qa="enabled-checkbox-label">{{ $t("enabled") }}</p>
        <v-checkbox density="compact" color="primary" false-icon="ecc:ecc-D-QuadOFF" true-icon="ecc:ecc-D-QuadON"
          hide-details v-model="enabled" data-qa="enabled-checkbox" />
      </v-row>
      <!-- Condition -->
      <v-row class="my-0 pt-4 pb-2">
        <v-col cols="10" class="py-0" style="font-size: 18px">
          Alarm condition
        </v-col>
        <v-col cols="2" class="py-0"><AnimatedButton style="margin-left:12px" icon="ecc:ecc-T-Help" @click="showHelp" :disabled="!policy.type"></AnimatedButton></v-col>
      </v-row>
      <v-select
        v-model="policyType"
        :items="alarmPolicyType"
        :label="$t('conditionType')"
        @update:modelValue="showPolicy"
        hide-details
        class="py-2"
      ></v-select>
      <TriggerPolicyAnalogBandComponent
        v-if="policy.type == TriggerPolicyTypeEnum.ANALOG_BAND"
        :policy="policy"
        :deviceGroupsChosen="deviceGroupsChosen"
      />
      <TriggerPolicyOnChangeComponent
        v-if="policy.type == TriggerPolicyTypeEnum.ON_CHANGED"
        :policy="policy"
        :deviceGroupsChosen="deviceGroupsChosen"
      />
      <TriggerPolicyOnLevelComponent
        v-if="policy.type == TriggerPolicyTypeEnum.ON_LEVEL"
        :policy="policy"
        :deviceGroupsChosen="deviceGroupsChosen"
      />
      <TriggerPolicyConnectionComponent
        v-if="policy.type == TriggerPolicyTypeEnum.CONNECTION"
        :policy="policy"
        :deviceGroupsChosen="deviceGroupsChosen"
      />
      <!-- Action -->
      <v-row class="my-0 pt-4 pb-2">
        <v-col cols="10" class="py-0" style="font-size: 18px">
          Alarm on: actions
        </v-col>
        <v-col cols="2" class="py-0">
          <v-menu>
            <template v-slot:activator="{ props: menu }">
              <v-tooltip>
                <template v-slot:activator="{ props: tooltip }">
                  <v-btn
                    v-bind="mergeProps(menu, tooltip)"
                    class="bg-wt"
                    variant="plain"
                    density="compact"
                    @click="addAction"
                  >
                    <v-icon class="pr-1 col-dk" size="large">ecc:ecc-T-Add</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("addAction") }}</span>
              </v-tooltip>
            </template>
            <!-- menu options -->
            <v-list>
              <v-list-item
                @click="addAction(TriggerActionTypeEnum.EMAIL_ACTION)"
              >
                <v-list-item-title>{{ $t("emailAction") }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="addAction(TriggerActionTypeEnum.WEBHOOK_ACTION)"
              >
                <v-list-item-title>{{$t("webhookAction")}}</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="addAction(TriggerActionTypeEnum.TELEGRAM_ACTION)"
              >
                <v-list-item-title>{{$t("telegramAction")}}</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="addAction(TriggerActionTypeEnum.TEAMS_ACTION)"
              >
                <v-list-item-title>{{$t("teamsAction")}}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <v-row
        class="list-group-action my-0 pr-4"
        :class="{
          active: selectedAction && action.actionId == selectedAction.actionId,
        }"
        v-for="action in actions"
        :key="action.actionId"
        @click="selectAction(action)"
        align-center
      >
        <v-col cols="1" class="py-2">
          <v-icon class="action-icon pr-2" >ecc:ecc-TRIG-action</v-icon>
        </v-col>
        <v-col cols="1" class="py-2">
          <v-tooltip :text="$t('table.enabled') + ': ' + String(action.enabled !== false)" location="top">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props">
                {{ action.enabled === false ? 'ecc:ecc-E-Copy-copia-2' : 'ecc:ecc-E-DeActive' }}
              </v-icon>
            </template>
          </v-tooltip>
        </v-col>  
        <v-col cols="9" class="py-2">
          <div class="action-text pr-2">
            {{ prettyActionType(action.type) }}
          </div>
        </v-col>
        <v-col cols="1" class="py-2">
          <DeleteAction
            :id="action.actionId"
            :name="prettyActionType(action.type)"
            @actionDeleteOpen="onActionDeleteOpen"
            @actionDeleted="onActionDeleted"
            @cancelClicked="onCancelClicked"
          />
        </v-col>
      </v-row>
      <v-row
        v-if="type.id === 'ACK' || type.id === 'ACK_RESET'"
        class="my-0 pt-4 pb-2"
      >
        <v-col cols="10" class="py-0" style="font-size: 18px"> Ack url </v-col>
        <v-col cols="2" class="py-0"><AnimatedButton style="margin-left:12px" icon="ecc:ecc-T-Help" @click="showHelpUrl"></AnimatedButton></v-col>
      </v-row>
      <v-row
        v-if="type.id === 'ACK' || type.id === 'ACK_RESET'"
        class="my-0 pb-2"
      >
        <v-col class="py-0">
          {{ eventsUrl + ackUuid }}
        </v-col>
      </v-row>
      <v-row v-if="type.id === 'ACK_RESET'" class="my-0 pt-4 pb-2">
        <v-col class="py-0" style="font-size: 18px"> Reset url </v-col>
      </v-row>
      <v-row v-if="type.id === 'ACK_RESET'" class="my-0 pb-2">
        <v-col class="py-0">
          {{ eventsUrl + resetUuid }}
        </v-col>
      </v-row>
    </OperationalComponent>
  </v-navigation-drawer>
  <ConfigureAction
    :action="selectedAction"
    :view="viewConfigureAction"
    @actionConfigured="storeAction"
  ></ConfigureAction>
  <HelpSideDrawer id="HelpEditSideDrawer">
    <div v-html="sideHelp"></div>
  </HelpSideDrawer>
</template>

<script lang="ts">
import { dataStore } from "../stores/data";
import { mergeProps } from "vue";
import OperationalComponent from "./UIComponents/OperationalComponent.vue";
import { v4 as uuidv4 } from "uuid";
import AnimatedButton from "../components/UIComponents/AnimatedButton.vue";
import {
  ALARM_TYPES_FE,
  ALARM_SEVERITY,
  ALARM_STRUCT,
  constants,
} from "../utils/type.util";
import {
  ALARM_POLICY_TYPE,
  TriggerPolicyTypeEnum,
  TriggerPolicyConnectionSourceEnum,
  TriggerPolicyConnectionStatusEnum,
  TriggerPolicyLevelMaskEnum,
  TriggerPolicyChangeMaskEnum,
  TriggerPolicyAnalogBand,
  TriggerPolicyOnChange,
  TriggerPolicyOnLevel,
  TriggerPolicyConnection,
  ExistingPolicies,
} from "./Policies/type.util";
import {
  ALARM_ACTION_TYPE,
  TriggerActionTypeEnum,
  AvailableActions,
  TriggerEmailEventContentDTO,
  TriggerEmailAction,
  TriggerWebhookEventContentDTO,
  TriggerWebhookAction,
  TriggerTelegramEventContentDTO,
  TriggerTelegramAction,
  TriggerTeamsEventContentDTO,
  TriggerTeamsAction,
} from "./Actions/type.util";
import DeleteAction from "./Policies/DeleteAction.vue";
import TriggerPolicyAnalogBandComponent from "./Policies/TriggerPolicyAnalogBandComponent.vue";
import TriggerPolicyOnChangeComponent from "./Policies/TriggerPolicyOnChangeComponent.vue";
import TriggerPolicyOnLevelComponent from "./Policies/TriggerPolicyOnLevelComponent.vue";
import TriggerPolicyConnectionComponent from "./Policies/TriggerPolicyConnectionComponent.vue";
import ConfigureAction from "./Actions/ConfigureAction.vue";
import { policyAndReverse } from "../utils/policyAndReverse";
import { store as HelpStore } from "../components/Help/Help.store";
import HelpSideDrawer from "./Help/HelpSideDrawer.vue";
import PoliciesHelp from "./Help/PoliciesHelp";
import ActionsHelp from "./Help/ActionsHelp";
import { IDeviceGroups } from "../dtos/deviceGroups.dto";

export default {
  emits: ["alarmUpdated"],
  data() {
    return {
      helpStore: HelpStore,
      dataStore: dataStore(),
      alarmId: '',
      width: "0px",
      name: "",
      description: "",
      enabled: true,
      isFetching: false,
      nameErrorMessage: '',
      confirmButtonEnabled: false,
      alarmTypes: ALARM_TYPES_FE,
      type: ALARM_TYPES_FE[0],
      alarmSeverity: ALARM_SEVERITY,
      severity: ALARM_SEVERITY[0],
      alarmPolicyType: ALARM_POLICY_TYPE,
      policyType: ALARM_POLICY_TYPE[0],
      alarmActionType: ALARM_ACTION_TYPE,
      action: ALARM_ACTION_TYPE[0],
      TriggerPolicyTypeEnum,
      TriggerActionTypeEnum,
      mergeProps,
      policy: {
        type: ALARM_POLICY_TYPE[0].value,
      } as ExistingPolicies,
      deviceGroupsChosen: [] as IDeviceGroups[],
      actions: [] as AvailableActions[],
      alarm: {},
      selectedAction: undefined as unknown as AvailableActions,
      deletingAction: undefined,
      eventsUrl: "",
      ackUuid: "",
      resetUuid: "",
      helpType: '',
    };
  },
  components: {
    OperationalComponent,
    DeleteAction,
    TriggerPolicyAnalogBandComponent,
    TriggerPolicyConnectionComponent,
    TriggerPolicyOnChangeComponent,
    TriggerPolicyOnLevelComponent,
    ConfigureAction,
    AnimatedButton,
    HelpSideDrawer,
  },
  methods: {
    showHelp($event) {
      this.dataStore.closeStateMachine($event);
      if (this.helpType == 'policy') {
        this.helpStore.drawerItems["HelpEditSideDrawer"]?.toggle($event);
      } else {
        this.helpType = 'policy';
        if (!this.helpStore.drawerItems["HelpEditSideDrawer"]?.showDrawer) {
          this.helpStore.drawerItems["HelpEditSideDrawer"]?.toggle($event);
        };
      }
    },
    showHelpUrl($event) {
      this.dataStore.closeStateMachine($event);
      if (this.helpType == 'url') {
        this.helpStore.drawerItems["HelpEditSideDrawer"]?.toggle($event);
      } else {
        this.helpType = 'url';
        if (!this.helpStore.drawerItems["HelpEditSideDrawer"]?.showDrawer) {
          this.helpStore.drawerItems["HelpEditSideDrawer"]?.toggle($event);
        };
      }
    },
    async onCreateClick(event) {
      this.isFetching = true;

      const events = this.alarm.events.map((e) =>
        policyAndReverse(e, this.policy, this.deviceGroupsChosen)
      );
      const states = this.alarm.states.map((s) => {
        if (s.state === "ALARM ON") {
          s.actions = this.actions.map((a) => {
            let params;
            switch(a.type) {
              case TriggerActionTypeEnum.WEBHOOK_ACTION:
                params = a.webhookConfig;
                break;
              case TriggerActionTypeEnum.EMAIL_ACTION:
                params = a.emailConfig;
                break;
              case TriggerActionTypeEnum.TELEGRAM_ACTION:
                params = a.telegramConfig;
                break;
              case TriggerActionTypeEnum.TEAMS_ACTION:
                params= a.teamsConfig;
                break;
            }
            return {
              type: a.type,
              actionId: a.actionId,
              enabled: a.enabled,
              params,
            };
          });
        }
        return s;
      });
      const alarm = {
        alarmId: this.alarmId,
        name: this.name,
        type: this.type.id,
        enabled: this.enabled,
        description: this.description,
        nInstances: 1,
        severity: this.severity.value,
        states,
        events,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: this.$i18n.locale,
        transitions: this.alarm.transitions,
      };
      await this.dataStore.updateAlarm(alarm);

      this.isFetching = false;
      this.selectedAction = undefined;
      this.$emit("alarmUpdated", true);
      this.dataStore.closeEdit(event);
    },
    checkConfirmButtonEnabled() {
      this.nameErrorMessage = '';
      if (this.name.trim().length < 1) {
        this.nameErrorMessage = this.$('nameFieldMandatory');
        this.confirmButtonEnabled = false;
        return;
      } else {
        if (this.dataStore.getAlarms?.content) {
          for (const alarm of this.dataStore.getAlarms.content) {
            const { alarmId, name } = alarm;
            if (this.name.trim() === name && alarmId !== this.alarmId) {
              this.nameErrorMessage = this.$('alarmAlreadyExists', {alarmName: name});
            }
          }
        }
      }

      if (this.nameErrorMessage != '') {
        this.confirmButtonEnabled = false;
        return;
      }

      if (
        this.policyType !== TriggerPolicyTypeEnum.CONNECTION &&
        this.policy.modelPathMatcher?.trim().length < 1
      ) {
        this.confirmButtonEnabled = false;
        return;
      }

      this.confirmButtonEnabled = true;
    },
    toggleShowedState($event) {
      this.alarm = ALARM_STRUCT[this.type.id];
      this.dataStore.toggleShowedState($event, this.type);
    },
    toggleStateMachine($event) {
      this.dataStore.toggleStateMachine($event, this.type);
    },
    storeAction(action: AvailableActions) {
      const pos = this.actions.findIndex((a) => a.actionId == action.actionId);
      this.actions.splice(pos, 1, action);
      this.selectedAction = undefined;
    },
    addAction(type) {
      switch (type) {
        case TriggerActionTypeEnum.EMAIL_ACTION:
          this.addActionEmail();
          break;
        case TriggerActionTypeEnum.WEBHOOK_ACTION:
          this.addActionWebhook();
          break;
        case TriggerActionTypeEnum.TELEGRAM_ACTION:
          this.addActionTelegram();
          break;
        case TriggerActionTypeEnum.TEAMS_ACTION:
          this.addActionTeams();
          break;
        default:
          return null;
      }
    },
    addActionEmail(): void {
      const triggerEmailEventContent: TriggerEmailEventContentDTO = {
        emailTo: "",
        emailFrom: "",
        emailBcc: "",
        subject: "",
        template: "",
        xdays: "",
      };
      const action: TriggerEmailAction = {
        type: TriggerActionTypeEnum.EMAIL_ACTION,
        stateful: false,
        emailConfig: triggerEmailEventContent,
        actionId: uuidv4(),
        enabled: true,
      };
      this.actions.push(action);
      this.selectedAction = action;
    },
    addActionWebhook(): void {
      const triggerWebhookEventContentDTO: TriggerWebhookEventContentDTO = {
        headers: {},
        method: "",
        url: "",
        template: "",
      };
      const action: TriggerWebhookAction = {
        type: TriggerActionTypeEnum.WEBHOOK_ACTION,
        stateful: false,
        webhookConfig: triggerWebhookEventContentDTO,
        actionId: uuidv4(),
        enabled: true,
      };
      this.actions.push(action);
      this.selectedAction = action;
    },
    addActionTelegram(): void {
      const triggerTelegramEventContentDTO: TriggerTelegramEventContentDTO = {
        token: "",
        chatId: "",
        template: "",
      };
      const action: TriggerTelegramAction = {
        type: TriggerActionTypeEnum.TELEGRAM_ACTION,
        stateful: false,
        telegramConfig: triggerTelegramEventContentDTO,
        actionId: uuidv4(),
        enabled: true,
      };
      this.actions.push(action);
      this.selectedAction = action;
    },
    addActionTeams(): void {
      const triggerTeamsEventContentDTO: TriggerTeamsEventContentDTO = {
        endpoint: "",
        template: "",
      };
      const action: TriggerTeamsAction = {
        type: TriggerActionTypeEnum.TEAMS_ACTION,
        stateful: false,
        teamsConfig: triggerTeamsEventContentDTO,
        actionId: uuidv4(),
        enabled: true,
      };
      this.actions.push(action);
      this.selectedAction = action;
    },
    showPolicy(type: string): void {
      switch (type) {
        case TriggerPolicyTypeEnum.ANALOG_BAND:
          this.showPolicyAnalogBand();
          break;
        case TriggerPolicyTypeEnum.ON_CHANGED:
          this.showPolicyOnChange();
          break;
        case TriggerPolicyTypeEnum.ON_LEVEL:
          this.showPolicyOnLevel();
          break;
        case TriggerPolicyTypeEnum.CONNECTION:
          this.showPolicyConnection();
          break;
        default:
          return;
      }
    },
    showPolicyAnalogBand(): void {
      this.policy = {
        type: TriggerPolicyTypeEnum.ANALOG_BAND,
        modelPathMatcher: "",
        inside: true,
        min: 0,
        max: 0,
        uuid: uuidv4(),
        time: 0,
      } as TriggerPolicyAnalogBand;
    },
    showPolicyOnChange(): void {
      this.policy = {
        type: TriggerPolicyTypeEnum.ON_CHANGED,
        modelPathMatcher: "",
        changeMask: TriggerPolicyChangeMaskEnum.VALUE_CHANGED,
        skipFirstNChanges: 0,
        deadband: 0,
        isPercentage: true,
        uuid: uuidv4(),
      } as TriggerPolicyOnChange;
    },
    showPolicyOnLevel(): void {
      this.policy = {
        type: TriggerPolicyTypeEnum.ON_LEVEL,
        modelPathMatcher: "",
        mode: TriggerPolicyLevelMaskEnum.ON_ENTER,
        min: NaN,
        max: NaN,
        skipFirstChanges: 0,
        level: "",
        uuid: uuidv4(),
      } as TriggerPolicyOnLevel;
    },
    showPolicyConnection(): void {
      this.policy = {
        type: TriggerPolicyTypeEnum.CONNECTION,
        source: TriggerPolicyConnectionSourceEnum.VPN,
        status: TriggerPolicyConnectionStatusEnum.ON,
        time: 0,
      } as TriggerPolicyConnection;
    },
    selectAction(action: AvailableActions) {
      let actionAlreadySelected = this.selectedAction;
      this.selectedAction = undefined;
      if (
        !this.deletingAction &&
        (!actionAlreadySelected ||
          action.actionId !== actionAlreadySelected.actionId)
      ) {
        this.selectedAction = action;
      }
    },
    prettyActionType(type: string): string {
      switch (type) {
        case TriggerActionTypeEnum.EMAIL_ACTION:
          return this.$t("emailAction");
        case TriggerActionTypeEnum.WEBHOOK_ACTION:
          return this.$t("webhookAction");
        case TriggerActionTypeEnum.TELEGRAM_ACTION:
          return this.$t("telegramAction");
        case TriggerActionTypeEnum.TEAMS_ACTION:
          return this.$t("teamsAction");
        default:
          return "N.A.";
      }
    },
    onActionDeleted(actionId: string): void {
      this.actions = this.actions.filter((a) => a.actionId != actionId);
      this.deletingAction = undefined;
      this.selectedAction = undefined;
    },
    onCancelClicked(_: string): void {
      this.deletingAction = undefined;
      this.selectedAction = undefined;
    },
    onActionDeleteOpen(actionId: string): void {
      this.deletingAction = actionId;
    },
    closeDialog(event): void {
      this.selectedAction = undefined;
      this.dataStore.closeEdit(event);
    },
  },
  watch: {
    name(v) {
      this.checkConfirmButtonEnabled();
    },
    async "dataStore.getShowEditPanel"(v) {
      if (v) {
        this.alarmId = this.dataStore.getEditAlarmId;
        const alarm = await this.dataStore.fetchAlarm(this.alarmId);
        this.name = alarm.name;
        this.description = alarm.description;
        this.enabled = alarm.enabled;
        this.type = ALARM_TYPES_FE.find((a) => a.id === alarm.type);
        this.severity = ALARM_SEVERITY.find((a) => a.value === alarm.severity);
        this.policy = alarm.events.find(
          (e) => e.label === "CONDITION ON"
        )?.policy;
        const deviceGroups = alarm.events.find(
          (e) => e.label === "CONDITION ON"
        )?.deviceGroups;
        this.deviceGroupsChosen = deviceGroups
          ? this.dataStore.getDeviceGroups.filter((dg) =>
              deviceGroups.includes(dg.name)
            )
          : [];
        this.policyType = ALARM_POLICY_TYPE.find(
          (p) => p.value === this.policy.type
        );
        this.alarm.transitions = alarm.transitions;
        this.alarm.states = alarm.states;
        this.alarm.events = alarm.events;
        this.actions = alarm.states
          .find((s) => s.state === "ALARM ON")
          ?.actions.map((a) => {
            switch(a.type) {
              case TriggerActionTypeEnum.WEBHOOK_ACTION:
                return {
                  type: a.type,
                  webhookConfig: a.params,
                  actionId: a.actionId,
                  enabled: a.enabled,
                };
              case TriggerActionTypeEnum.EMAIL_ACTION:
                return {
                  type: a.type,
                  emailConfig: a.params,
                  actionId: a.actionId,
                  enabled: a.enabled,
                };
              case TriggerActionTypeEnum.TELEGRAM_ACTION:
                return {
                  type: a.type,
                  telegramConfig: a.params,
                  actionId: a.actionId,
                  enabled: a.enabled,
                };
              case TriggerActionTypeEnum.TEAMS_ACTION:
                return {
                  type: a.type,
                  teamsConfig: a.params,
                  actionId: a.actionId,
                  enabled: a.enabled,
                };
            }
          });
        this.ackUuid = alarm.events.find(
          (e) => e.label === "ACK RECEIVED"
        )?.eventId;
        this.resetUuid = alarm.events.find(
          (e) => e.label === "RESET RECEIVED"
        )?.eventId;
        this.eventsUrl = `${constants.serviceUrl}/${alarm.instanceId}/${alarm.organizationId}/events/`;
        switch (this.$vuetify.display.name) {
          case "xs":
            this.width = "100%";
          case "sm":
            this.width = "50%";
          default:
            this.width = "500px";
        }
      } else {
        this.width = "0px";
      }
    },
  },
  computed: {
    viewConfigureAction() {
      return this.selectedAction !== undefined;
    },
    sideHelp(): string {
      if (this.helpType == 'policy') {
        switch (this.policy.type) {
          case TriggerPolicyTypeEnum.ON_LEVEL:
            return PoliciesHelp.getLocaleOnLevelHelp();
          case TriggerPolicyTypeEnum.ON_CHANGED:
            return PoliciesHelp.getLocaleOnChangedHelp();
          case TriggerPolicyTypeEnum.CONNECTION:
            return PoliciesHelp.getLocaleConnectionHelp();
          case TriggerPolicyTypeEnum.ANALOG_BAND:
            return PoliciesHelp.getLocaleAnalogBandHelp();
          default:
            return "";
        }
      } else {
        return ActionsHelp.getLocaleUrlHelp();
      }
    },
  },
};
</script>

<style scoped>
.v-text-field {
  margin-bottom: 0px;
}

.list-group-item {
  width: 100%;
}
.action-text {
  align-self: center;
}
.list-group-action-action {
  display: none;
  padding-right: 30px;
}
.list-group-action {
  position: relative;
  border-top: 1px solid var(--color-g2);
  cursor: pointer;
}
.list-group-action:hover .list-group-action-action {
  display: inline-flex;
  position: absolute;
  right: 0px;
  cursor: pointer;
}
.action-group {
  width: 100%;
}

.active .action-text,
.active .action-icon {
  color: var(--color-highlight);
}

.v-btn--icon.v-btn--density-default.squareCorners {
  border-radius: 0;
  font-size: 49px;
  height: 40px !important;
  width: 45px;
  margin-right: 12px;
}

.checkbox-label {
  line-height: 40px;
  width: 60px;
}
</style>
